import { useEffect, useRef, useState } from 'react';
import type { DragEvent, MouseEvent } from 'react';
import { Link, NavLink, useMatch, useNavigate } from 'react-router';
import {
  SunIcon,
  MoonIcon,
  SparkleIcon,
  SidebarSimpleIcon,
  GaugeIcon,
  MagnifyingGlassIcon,
  CommandIcon,
  DotsSixVerticalIcon,
} from '@phosphor-icons/react';
import { useTheme } from '~/lib/stores/theme-store';
import { useIsAdmin, useSuperAdmin } from '~/hooks/queries/use-is-admin';
import { EddyMark, EddyWordmark } from './eddy-mark';
import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { masterKeys } from '~/lib/query-keys';
import { Button } from '~/components/ui/button';
import { useAttention } from '~/hooks/queries/use-attention';
import { useBackgroundTasks } from '~/hooks/queries/use-inspector';
import { useCronAuthStatus } from '~/hooks/mutations/use-cron-mutations';
import { cronsNeedingAuth } from '~/features/crons/cron-list';
import { useEnforcedBudget } from '~/hooks/queries/use-budget';
import { SidebarBudget } from '~/features/limits/budget-meter';
import { PoweredByBlockbrain } from './powered-by-blockbrain';
import { cn } from '~/lib/utils';
import { useSidebarStore } from '~/lib/stores/sidebar-store';
import { useProBuilderOpenStore } from '~/lib/stores/pro-builder-open-store';
import { useFeatureFlags, resolveFeatureFlag, resolveDestinationFlag, SIDEBAR_DEFAULT_COLLAPSED_FLAG } from '~/hooks/queries/use-feature-flags';
import {
  SIDEBAR_DESTINATIONS,
  isSidebarDestinationVisible,
  type SidebarDestination,
  type SidebarAttentionKey,
} from './sidebar-nav-catalog';
import { Tip } from '~/components/ui/tip';

/**
 * FRONT-1485: what the rail can badge.
 *
 * Everything here is a QUEUE — something a human still has to decide on. The
 * inventory totals that used to live alongside these (files, agents, apps,
 * skills, tools) are gone: a number in a nav rail reads like unread mail, and
 * two of those totals also overstated their own page (see
 * `src/admin/counts-routes.ts`). Resource totals belong on the page that owns
 * them, where they are correct and in context.
 */
type Counts = {
  /** Memory inbox: pending proposals (minus commitments) + duplicate reviews. */
  memoryReview: number | null;
  /** Work: pending commitments awaiting a yes/no. */
  workNeedsOk: number | null;
  /** Knowledge: items that may be out of date; the number links to the review. */
  knowledgeReview: number | null;
  /** FRONT-634: the backing endpoint returns coding sessions + scheduled
   *  (cron) runs — chat-initiated tool/skill/delegate hops are filtered out
   *  server-side, so this is no longer a generic "tools in flight" count, but
   *  it is a mix of two kinds, not "coding" alone. Not a nav badge — it has its
   *  own "Active runs" row at the foot of the rail. */
  activeRuns: number | null;
};

// Per-item alert overrides. When set, the count badge renders in an amber
// "needs attention" style and the count value comes from here instead of the
// neutral total. Right now only `schedule` uses this — cron jobs whose auth has
// failed or gone missing — but the slot is generic so other items can opt in.
type Alerts = {
  schedule?: { count: number; tooltip: string };
};

function useCounts(): { counts: Counts; alerts: Alerts } {
  const { data } = useAttention();
  const { data: tasks } = useBackgroundTasks();
  const { data: authStatus, isSuccess: authStatusLoaded } = useCronAuthStatus();
  const needsAuth = cronsNeedingAuth(authStatus, authStatusLoaded);
  return {
    counts: {
      memoryReview: data?.memoryReview ?? null,
      workNeedsOk: data?.workNeedsOk ?? null,
      knowledgeReview: data?.knowledgeReview ?? null,
      activeRuns: tasks?.counts.active ?? null,
    },
    alerts: {
      schedule:
        needsAuth > 0
          ? {
              count: needsAuth,
              tooltip: `${needsAuth} scheduled job${needsAuth === 1 ? '' : 's'} need re-auth`,
            }
          : undefined,
    },
  };
}

const COLLAPSE_LS_KEY = 'eddy.sidebar.collapsed';

/**
 * Pure resolution behind `useCollapsed`'s initial state, factored out so it's
 * testable without mocking localStorage/useFeatureFlag. A stored preference
 * always wins; `defaultCollapsed` (the flag) only decides the fallback for a
 * browser that has never toggled the sidebar.
 */
export function resolveInitialCollapsed(stored: string | null, defaultCollapsed: boolean): boolean {
  if (stored === '1') return true;
  if (stored === '0') return false;
  return defaultCollapsed;
}

function useCollapsed() {
  // The flag is fetched over the network, so it can never be known at the
  // very first render — the initializer below can only use `false` (expanded)
  // as the pre-flag fallback. `hadStoredPreferenceOnMount` snapshots whether
  // THIS browser already had an explicit preference, captured once via a
  // useState initializer so it can never be flipped by the persist effect's
  // own write below (which runs on every mount, including this one).
  const [hadStoredPreferenceOnMount] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_LS_KEY) != null; } catch { return true; }
  });
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return resolveInitialCollapsed(localStorage.getItem(COLLAPSE_LS_KEY), false); } catch { return false; }
  });

  // True once `collapsed` reflects a real decision — an existing stored
  // preference, the flag's resolved value, or a manual toggle — so the effect
  // below applies the flag's default AT MOST ONCE, and never after a manual
  // toggle made while the flag was still loading.
  const settledRef = useRef(hadStoredPreferenceOnMount);

  const { data, isLoading, isError } = useFeatureFlags();
  useEffect(() => {
    if (settledRef.current || isLoading) return;
    settledRef.current = true;
    setCollapsed(resolveFeatureFlag(SIDEBAR_DEFAULT_COLLAPSED_FLAG, false, data, isLoading, isError));
  }, [data, isLoading, isError]);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_LS_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  return {
    collapsed,
    toggle: () => {
      settledRef.current = true;
      setCollapsed((v) => !v);
    },
  };
}

/** Opens the shared global command palette (`command-palette.tsx`) via the
 *  same synthetic ⌘K keydown dispatch `app-header.tsx` uses — the palette
 *  owns its own listener, so this is the whole integration surface. */
function openCommandPalette() {
  window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
}

/**
 * FRONT-3003: a destination that `opensProBuilderWindow` opens the Pro Builder
 * window instead of navigating (the link keeps its `/pro-builder` href so a
 * modified click still opens the deep link in a new tab).
 */
function onDestinationClick(e: MouseEvent<HTMLElement>, dest: SidebarDestination) {
  if (dest.opensProBuilderWindow) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    useProBuilderOpenStore.getState().openProBuilder();
    return;
  }
  scrollInboxToTopIfAlreadyThere(dest.id);
}

function scrollInboxToTopIfAlreadyThere(destId: string) {
  // Clicking Inbox while already on it scrolls back to the top — the Inbox is
  // the "home" surface so the user expects it to snap back to the overview,
  // not stay scrolled mid-feed.
  if (destId === 'inbox') {
    document.getElementById('inbox-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

/**
 * Runtime health as a single dot (`design_handoff_sidebar_header` §1.4).
 *
 * A status surface, so it swaps tone with real state and says why on hover —
 * and per the handoff it NEVER animates: a pulsing dot in the corner of the
 * chrome reads as "something is happening", which is the Inspector's job, not
 * health's. Tones are the app's canonical three (CLAUDE.md: one green, one
 * red, one amber), never the raw `--ok` / `--warn` scale, so this dot matches
 * every other state colour in the app.
 */
function HealthDot({
  status,
  size = 'md',
}: {
  status: 'alive' | 'off' | 'degraded';
  /** `sm` is for the collapsed rail: at 9px the dot plus its ring is almost as
   *  wide as the 17px mark it perches on, and reads as a blob rather than a
   *  status light. The handoff leaves the rail's details open (§5). */
  size?: 'sm' | 'md';
}) {
  const box = size === 'sm' ? 'h-[7px] w-[7px] ring-[2px]' : 'h-[9px] w-[9px] ring-[3px]';
  const tone = {
    alive: { dot: 'bg-success ring-success/20', label: 'All systems healthy' },
    degraded: { dot: 'bg-amber-500 ring-amber-500/20', label: 'Some systems degraded' },
    off: { dot: 'bg-destructive ring-destructive/20', label: 'Agent runtime offline' },
  }[status];
  return (
    <Tip label={tone.label}>
      <span
        role="img"
        aria-label={tone.label}
        // `inline-block` is load-bearing, not decoration: a bare <span> is
        // display:inline, which ignores width/height entirely. In the expanded
        // header it looked right only because a flex row blockifies its
        // children — in the collapsed rail, where it sits in a plain absolute
        // wrapper, the same markup drew a pale square instead of a dot.
        className={cn('inline-block shrink-0 rounded-full', box, tone.dot)}
      />
    </Tip>
  );
}

/**
 * A badge in the rail.
 *
 * `attention` (primary) is the FRONT-1485 default for a queue waiting on the
 * user; `alert` (amber) stays reserved for something BROKEN — a cron whose auth
 * has failed and which will not run until it is fixed. Two states people read
 * differently at a glance, so they are not collapsed into one colour: a dead
 * job must not look like three pending suggestions.
 *
 * `default` (neutral) survives for the "Active runs" indicator, which
 * reports what the system is doing rather than asking the user for anything.
 */
function CountBadge({
  value,
  tone,
  title,
  linked = false,
}: {
  value: number | null;
  tone: 'default' | 'active' | 'alert' | 'attention';
  title?: string;
  /** The number is its own link: it gets a stronger background while that link is hovered. */
  linked?: boolean;
}) {
  if (value == null) return null;
  // An empty queue is not news. Only the neutral "Active runs" indicator
  // still shows a zero, because there "0" is a live status reading, not a
  // false alarm — every other badge appearing at all is the signal (the old
  // inventory badges printed a literal "Skills 0", a badge advertising
  // nothing).
  if (value === 0 && tone !== 'default') return null;
  return (
    <Tip label={title}>
      <span
        className={cn(
          'font-mono text-xs font-medium leading-none px-[7px] py-[1px] rounded-full tabular-nums shrink-0',
          tone === 'alert' && 'bg-[var(--warn-dim)] text-[var(--warn)]',
          tone === 'attention' && 'bg-primary/10 text-primary',
          tone === 'active' && 'bg-primary/10 text-primary',
          tone === 'default' && 'bg-[var(--bg-3)] text-[var(--ink-2)]',
          linked && 'group-hover/badge:bg-primary/15',
          // The ring sits on the pill, not on the link's full-row-height click target.
          linked && 'group-focus-visible/badge:ring-[3px] group-focus-visible/badge:ring-ring/50',
        )}
      >
        {/* Capped so a heavy queue can't widen the rail. Dev's busiest user
            sits at 49 today, but the badge must not be the thing that breaks
            the layout for whoever first crosses three digits. */}
        {value > 99 ? '99+' : value}
      </span>
    </Tip>
  );
}

function trailingSlotFor(dest: SidebarDestination, counts: Counts, alerts: Alerts, active: boolean) {
  if (dest.hasAlert && alerts.schedule) {
    return <CountBadge value={alerts.schedule.count} tone="alert" title={alerts.schedule.tooltip} />;
  }
  if (!dest.attentionKey) return null;
  // A badge that is its own link is rendered by NavRow beside the row's link
  // (never inside it), so it must not also render here.
  if (attentionLinkFor(dest, counts)) return null;
  return (
    <CountBadge
      value={counts[dest.attentionKey]}
      tone={active ? 'active' : 'attention'}
      title={attentionTooltip(dest.attentionKey, counts[dest.attentionKey])}
    />
  );
}

/** Says what the number IS, since a bare digit in a rail explains nothing. */
function attentionTooltip(key: SidebarAttentionKey, value: number | null): string | undefined {
  if (value == null || value === 0) return undefined;
  const plural = value === 1 ? '' : 's';
  if (key === 'knowledgeReview') {
    return `${value} knowledge item${plural} may be out of date — click to review`;
  }
  return key === 'memoryReview'
    ? `${value} memory suggestion${plural} to review`
    : `${value} commitment${plural} needing your OK`;
}

/** The accessible name of a badge link, per attention key; undefined = this key has no link. */
function attentionLinkLabel(key: SidebarAttentionKey, value: number): string | undefined {
  if (key === 'knowledgeReview') {
    return `Review ${value} out-of-date knowledge item${value === 1 ? '' : 's'}`;
  }
  return undefined;
}

/** The badge-as-its-own-link decision for a destination: set only when the
 *  destination declares an `attentionHref` AND its count is a positive number
 *  (hidden at 0 and when unknown, like every other attention badge). */
export function attentionLinkFor(
  dest: SidebarDestination,
  counts: Counts,
): { href: string; value: number; label: string; tooltip: string } | null {
  if (!dest.attentionHref || !dest.attentionKey) return null;
  const value = counts[dest.attentionKey];
  if (value == null || value <= 0) return null;
  const tooltip = attentionTooltip(dest.attentionKey, value);
  const label = attentionLinkLabel(dest.attentionKey, value);
  // A key with no link label defined gets no link at all, never a wrong one.
  if (!tooltip || !label) return null;
  return {
    href: dest.attentionHref,
    value,
    label,
    tooltip,
  };
}

/** A single draggable nav row. Drag affordances (the grip + native `draggable`
 *  drag events) only apply when `onDragStartRow` is supplied — the collapsed
 *  rail renders icon-only rows with no drag support at all.
 *
 *  FRONT-2381: the inter-row spacing is `pb-0.5` on THIS wrapper rather than a
 *  `gap` on the list container — a flex `gap` is dead space no element owns,
 *  so a `dragover` there fell through to the container's own handler and
 *  silently read as "drop at the end of the list" no matter which two rows it
 *  sat between. Padding is inside the wrapper's own box, so that same sliver
 *  now belongs to (and reports dragover for) the row above it. */
export function NavRow({
  dest,
  counts,
  alerts,
  dragging = false,
  dropBefore = false,
  onDragStartRow,
  onDragEndRow,
  onDragOverRow,
}: {
  dest: SidebarDestination;
  counts: Counts;
  alerts: Alerts;
  dragging?: boolean;
  dropBefore?: boolean;
  onDragStartRow?: (e: DragEvent<HTMLElement>) => void;
  onDragEndRow?: () => void;
  onDragOverRow?: (e: DragEvent<HTMLDivElement>) => void;
}) {
  const Icon = dest.icon;
  const draggableRow = !!onDragStartRow;
  const attentionLink = attentionLinkFor(dest, counts);
  // The badge link sits outside the NavLink, so it cannot read `isActive` from
  // it; ask the router the same question the NavLink asks.
  const rowActive = !!useMatch({ path: dest.path, end: dest.end ?? false });
  return (
    <div className={cn('relative pb-0.5', attentionLink && 'group/row')} onDragOver={onDragOverRow}>
      {dropBefore && (
        <div className="absolute -top-px left-2 right-2 h-0.5 rounded-full bg-primary z-10" />
      )}
      <NavLink
        to={dest.path}
        end={dest.end}
        draggable={draggableRow}
        onDragStart={onDragStartRow}
        onDragEnd={onDragEndRow}
        onClick={(e) => onDestinationClick(e, dest)}
        className={({ isActive }) =>
          cn(
            'group flex items-center gap-2 rounded-lg px-2 py-[7px] transition-colors duration-[120ms]',
            draggableRow && 'cursor-grab',
            dragging && 'opacity-35',
            isActive ? 'bg-primary/10 text-primary' : 'hover:bg-[var(--bg-3)]',
            // Hovering the badge link (a sibling, not a child) keeps the row highlighted.
            attentionLink && !isActive && 'group-hover/row:bg-[var(--bg-3)]',
            !isActive && 'text-[var(--ink-1)]',
            // Leave room for the absolutely-positioned badge link below.
            attentionLink && 'pr-12',
          )
        }
      >
        {({ isActive }) => (
          <>
            {draggableRow && (
              <span className="flex items-center text-[var(--ink-4)] cursor-grab shrink-0">
                <DotsSixVerticalIcon size={12} weight="bold" />
              </span>
            )}
            <Icon size={15} weight="regular" className="shrink-0" />
            <span
              className={cn('flex-1 truncate text-[14.5px]', isActive ? 'font-medium' : 'font-normal')}
            >
              {dest.label}
            </span>
            {trailingSlotFor(dest, counts, alerts, isActive)}
          </>
        )}
      </NavLink>
      {attentionLink && (
        <Tip label={attentionLink.tooltip}>
          <Link
            to={attentionLink.href}
            draggable={false}
            aria-label={attentionLink.label}
            data-testid={`sidebar-attention-link-${dest.id}`}
            className={cn(
              'group/badge absolute right-2 top-0 bottom-0.5 flex items-center rounded-full outline-none transition-opacity duration-[120ms]',
              dragging && 'opacity-35',
            )}
          >
            <CountBadge value={attentionLink.value} tone={rowActive ? 'active' : 'attention'} linked />
          </Link>
        </Tip>
      )}
    </div>
  );
}

function SearchTrigger() {
  return (
    <div className="px-2.5 pt-3 pb-1.5">
      <Tip label="Search & commands (⌘K)">
        <button
          type="button"
          onClick={openCommandPalette}
          className="flex w-full items-center gap-2.5 rounded-[10px] border border-[var(--line)] bg-[var(--bg-2)] px-2.5 py-2 text-left transition-colors hover:bg-[var(--bg-3)] cursor-pointer"
        >
          <MagnifyingGlassIcon size={15} className="text-[var(--ink-2)] shrink-0" />
          <span className="flex-1 text-[13.5px] text-[var(--ink-2)] truncate">Search or jump to…</span>
          <span className="flex items-center gap-0.5 rounded-md border border-[var(--line)] bg-[var(--bg-1)] px-1.5 py-0.5 font-mono text-[length:var(--text-2xs)] text-[var(--ink-3)] shrink-0">
            <CommandIcon size={10} />K
          </span>
        </button>
      </Tip>
    </div>
  );
}

interface DragTarget {
  /** Row id to insert before, or null to append (dropped on the list's own
   *  empty background rather than on a specific row). */
  id: string | null;
}

/**
 * FRONT-2381 — the sidebar's one order, shared by the expanded list and the
 * collapsed rail so the two can never disagree (both render from a single
 * call to this in `MissionControlSidebar`, never their own independent one).
 * The user's stored order (already filtered to known ids by
 * `sidebar-store.ts`'s hydrate) leads, in stored order; every remaining
 * visible destination the user has never explicitly reordered follows, in
 * fixed catalog order (`SIDEBAR_DESTINATIONS`) — the same fallback the old
 * "More" zone used for its own (unordered) contents. An id in `storedOrder`
 * that isn't currently visible (gated off, or removed from the catalog) is
 * silently skipped rather than rendered.
 */
export function resolveSidebarOrder(
  storedOrder: string[],
  visibleDestinations: SidebarDestination[],
): SidebarDestination[] {
  const byId = new Map(visibleDestinations.map((d) => [d.id, d]));
  const seen = new Set<string>();
  const ordered: SidebarDestination[] = [];
  for (const id of storedOrder) {
    const dest = byId.get(id);
    if (dest && !seen.has(id)) {
      ordered.push(dest);
      seen.add(id);
    }
  }
  for (const dest of visibleDestinations) {
    if (!seen.has(dest.id)) {
      ordered.push(dest);
      seen.add(dest.id);
    }
  }
  return ordered;
}

/**
 * Pure drop-reorder computation for the sidebar's drag-and-drop (mirrors
 * `briefing-feed.tsx`'s `reorderSections` — FRONT-1090 Part B — over the
 * sidebar's drag reorder, FRONT-285). Takes the CURRENT FULL order (every
 * visible destination, already resolved via `resolveSidebarOrder`) rather
 * than the raw persisted array, so a target the user has never explicitly
 * reordered — and which may therefore be absent from `sidebar-store`'s
 * stored order — still has a real position to insert before. FRONT-2381: the
 * old `moveToFrequent` computed positions against the stored array alone,
 * which would have silently misplaced a drop whenever the target wasn't in
 * it once the sidebar stopped being split into a curated zone plus a fixed
 * one.
 *
 * `targetId === draggedId` (dropping a row onto itself) is a no-op: returns
 * the exact same array reference passed in, so a caller can tell "nothing to
 * persist" with `next !== order` rather than a second equality check.
 * `targetId === null` appends `draggedId` at the end (the empty drop-zone
 * area below the last row). Otherwise `draggedId` is removed from wherever
 * it sits and reinserted just before `targetId`.
 */
export function computeDropOrder(order: string[], draggedId: string, targetId: string | null): string[] {
  if (targetId === draggedId) return order;
  const without = order.filter((id) => id !== draggedId);
  if (targetId == null) return [...without, draggedId];
  const index = without.indexOf(targetId);
  const insertAt = index === -1 ? without.length : index;
  return [...without.slice(0, insertAt), draggedId, ...without.slice(insertAt)];
}

/**
 * FRONT-2381 — carries forward any id that's in `storedOrder` but not part of
 * `visibleOrder` (gated off today by a permission/flag, or simply not
 * resolved as visible in this render), appending it after `visibleOrder` in
 * its stored relative position. `computeDropOrder` only ever sees the
 * resolved order of VISIBLE destinations, so persisting its result verbatim
 * would silently drop every currently-hidden id from storage on the very
 * next drag — and a destination hidden today would never reappear once
 * ungated. Returns `visibleOrder` itself (same reference) when nothing is
 * hidden, so a caller composing this with `computeDropOrder`'s own
 * reference-equality no-op check doesn't need a second one.
 */
export function appendHiddenIds(visibleOrder: string[], storedOrder: string[]): string[] {
  const visibleSet = new Set(visibleOrder);
  const hidden = storedOrder.filter((id) => !visibleSet.has(id));
  return hidden.length === 0 ? visibleOrder : [...visibleOrder, ...hidden];
}

export function MissionControlSidebar() {
  const { counts, alerts } = useCounts();
  const { isAdmin } = useIsAdmin();
  const { isSuperAdmin } = useSuperAdmin();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  // Build version stamp (FRONT-298) for the footer. Public /api/health carries
  // it; cache forever — it can't change within a session.
  const { data: health } = useQuery({
    queryKey: ['health-version'],
    queryFn: () => api.get<{ version?: string }>('/api/health'),
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const version = health?.version;
  // FRONT-1194: renders only past the org's first warning threshold, so a
  // healthy budget adds no permanent furniture to the rail.
  const budget = useEnforcedBudget();
  const { collapsed, toggle } = useCollapsed();
  // FRONT-1501: a SECOND subscription to the same cached query `useCollapsed`
  // already reads internally — TanStack Query dedupes by key, so this costs
  // no extra network call, and it's what lets `isSidebarDestinationVisible`
  // gate a destination's `flag` (Pro Builder, FRONT-3003, carries one; the
  // fallback is `false` so a flagged entry never flashes while flags load) the same way
  // `gate` already gates `isAdmin`/`isSuperAdmin`.
  const { data: flagsData, isLoading: flagsLoading, isError: flagsError } = useFeatureFlags();

  const { data: status } = useQuery({
    queryKey: masterKeys.status(),
    queryFn: () => api.get<{ status: string; bootedAt: string | null }>('/api/master/status'),
    refetchInterval: 30_000,
  });

  const masterStatus =
    status?.status === 'alive' ? 'alive' : status?.status === 'degraded' ? 'degraded' : 'off';

  const order = useSidebarStore((s) => s.order);
  const setOrder = useSidebarStore((s) => s.setOrder);

  const perms = {
    isAdmin,
    isSuperAdmin,
    isFlagEnabled: resolveDestinationFlag(flagsData, flagsLoading, flagsError),
  };
  const visibleDestinations = SIDEBAR_DESTINATIONS.filter((d) => isSidebarDestinationVisible(d, perms));
  // FRONT-2381: one flat, resolved order — the user's stored drag order first,
  // every other visible destination after it — shared by both the expanded
  // list below and the collapsed rail, so the two can never disagree.
  const orderedVisible = resolveSidebarOrder(order, visibleDestinations);

  // Drag & drop — transient, not persisted until drop (FRONT-285).
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DragTarget | null>(null);

  function handleDragStart(id: string) {
    return (e: DragEvent<HTMLElement>) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', id);
      setDragId(id);
    };
  }

  function handleDragEnd() {
    setDragId(null);
    setDropTarget(null);
  }

  function handleRowDragOver(id: string) {
    return (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!dragId) return;
      setDropTarget({ id });
    };
  }

  function handleZoneDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (!dragId) return;
    setDropTarget({ id: null });
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (!dragId || !dropTarget) {
      setDragId(null);
      setDropTarget(null);
      return;
    }
    // Computed from the resolved FULL order (every visible destination), not
    // the store's raw stored order — see `computeDropOrder`'s own docblock for
    // why that distinction is load-bearing. `next !== ids` is false for a
    // self-drop (dropping a row onto its own slot), so that case never calls
    // `setOrder` at all — no persist, no state change (FRONT-285 review fix,
    // carried over to the flat list). `appendHiddenIds` folds back in any id
    // `order` still holds that isn't currently visible, so a destination
    // gated off today keeps its stored place instead of being dropped from
    // storage the moment anything else is reordered.
    const ids = orderedVisible.map((d) => d.id);
    const next = computeDropOrder(ids, dragId, dropTarget.id);
    if (next !== ids) setOrder(appendHiddenIds(next, order));
    setDragId(null);
    setDropTarget(null);
  }

  if (collapsed) {
    return (
      <CollapsedSidebar
        masterStatus={masterStatus}
        onToggleCollapse={toggle}
        visibleOrdered={orderedVisible}
        theme={theme}
        toggleTheme={toggleTheme}
        activeRuns={counts.activeRuns}
        onOnboard={() => navigate('/onboard')}
      />
    );
  }

  return (
    // Framed shell: a full hairline + radius instead of the old single
    // border-right — the seam beside it is now canvas, not a shared rule.
    // `overflow-hidden` keeps the nav's scroll region inside the corners.
    <aside className="w-[268px] min-w-[268px] h-full flex flex-col overflow-hidden rounded-[var(--frame-radius)] border border-[var(--line)] bg-[var(--bg-1)] text-[14px] shadow-[var(--shadow-sm)]">
      {/*
        Identity bar — `design_handoff_sidebar_header` §1: wordmark, early-access pill,
        spacer, health dot, collapse. It replaced an avatar chip captioned
        "Eddy · Workspace · alive", which read like a tenant name but was a
        hardcoded string; the dot carries that state now, and the real brand
        mark carries the identity.

        Height stays 60, not the handoff's 64: the handoff picks 64 to optically
        centre against a 68px top bar, and ours is 61 — matching its bottom edge
        to this rule is the FRONT-1417 decision, so 64 here would misalign them
        for a rationale that doesn't apply. `min-h-[60px]` is referenced by name
        in `app-header.tsx`; move both together or neither.
      */}
      {/*
        Exact pixels, not Tailwind's rem scale: the app's root font-size is
        14px, so `gap-2.5` would be 8.75px here, not the handoff's 10.
      */}
      <div className="min-h-[60px] px-[14px] flex items-center gap-[10px] border-b border-[var(--line)]">
        <EddyWordmark size={13} className="shrink-0" />
        <span className="ml-[2px] shrink-0 whitespace-nowrap rounded-[6px] border border-[var(--eddy-blue-line)] bg-[var(--eddy-blue-soft)] px-[7px] py-[2px] text-[10.5px] font-semibold leading-none tracking-[0.3px] text-[var(--eddy-blue-text)]">
          EARLY ACCESS
        </span>
        <span className="flex-1" />
        <HealthDot status={masterStatus} />
        <Button
          onClick={toggle}
          variant="ghost"
          size="icon"
          className="text-[var(--ink-2)] hover:text-foreground h-[28px] w-[28px] rounded-[8px] shrink-0"
          tooltip="Collapse sidebar"
          aria-label="Collapse sidebar"
        >
          <SidebarSimpleIcon size={15} />
        </Button>
      </div>

      {/* Search trigger */}
      <SearchTrigger />

      {/* Scroll region — FRONT-2381: one flat list, no Frequent/More split. */}
      <div className="flex-1 overflow-y-auto px-2 pb-3 pt-1 flex flex-col">
        {/* `flex-1` (not `min-h-[40px]`) so the drop zone's own box stretches
            to fill the rest of the scroll area — otherwise the empty space
            under the last row belongs to this DIV'S PARENT, which has no drag
            handlers at all, and a drop there silently did nothing. No
            `gap` between rows: see `NavRow`'s own docblock for why that
            spacing moved into each row's padding instead. */}
        <div
          data-testid="sidebar-nav-list"
          onDragOver={handleZoneDragOver}
          onDrop={handleDrop}
          className="flex-1 flex flex-col rounded-[10px] p-0.5"
        >
          {orderedVisible.map((dest) => (
            <NavRow
              key={dest.id}
              dest={dest}
              counts={counts}
              alerts={alerts}
              dragging={dragId === dest.id}
              dropBefore={dropTarget?.id === dest.id}
              onDragStartRow={handleDragStart(dest.id)}
              onDragEndRow={handleDragEnd}
              onDragOverRow={handleRowDragOver(dest.id)}
            />
          ))}
          {/* The append target: shown only while a drag is live and the
              pointer is over the zone's own empty background rather than a
              specific row (`dropTarget.id === null`) — the same line style
              `NavRow` draws for "insert before this row", so append reads as
              one more instance of the same affordance rather than a second
              kind of indicator. */}
          {dragId && dropTarget?.id === null && (
            <div className="mx-2 h-0.5 rounded-full bg-primary shrink-0" aria-hidden="true" />
          )}
        </div>
      </div>

      {/* Onboarding + Active runs + theme toggle (FRONT-2381: Active runs moved
          here from the scroll region, replacing the removed More zone's row).
          FRONT-634 relabeled Active runs from "Active tools", then from
          "Active coding" — the backing endpoint counts coding sessions AND
          scheduled/cron runs together, so neither of those narrower labels
          was accurate. */}
      <div className="border-t border-[var(--line)] flex items-center justify-between gap-1 px-3 py-2">
        <Button
          onClick={() => navigate('/onboard')}
          tooltip="Re-run the onboarding wizard"
          variant="ghost"
          size="sm"
          className="text-[var(--ink-2)] hover:text-foreground h-7 px-2 text-xs gap-1.5"
        >
          <SparkleIcon size={14} />
          Onboarding
        </Button>
        <div className="flex items-center gap-0.5">
          {/* FRONT-2381: `role="img"` replaces this element's accessible name
              with its `aria-label` wholesale, so a screen reader never reaches
              the visible `CountBadge` text inside — the label has to carry
              the count itself, or a reader hears "Active runs" with no number
              at all. */}
          <Tip label={`Active runs: ${counts.activeRuns ?? '—'}`}>
            <div
              data-testid="sidebar-active-runs"
              role="img"
              aria-label={`Active runs: ${counts.activeRuns ?? '—'}`}
              className="flex items-center gap-1 h-7 px-1.5 text-[var(--ink-3)]"
            >
              <GaugeIcon size={13} weight="regular" className="shrink-0 opacity-70" />
              <span className="text-xs">Runs</span>
              <CountBadge value={counts.activeRuns} tone="default" />
            </div>
          </Tip>
          <Button
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            tooltip={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            variant="ghost"
            size="icon"
            className="text-[var(--ink-2)] hover:text-foreground h-7 w-7"
          >
            {theme === 'dark' ? <SunIcon size={16} /> : <MoonIcon size={16} />}
          </Button>
        </div>
      </div>

      {budget && <SidebarBudget budget={budget} />}

      {/* Footer: "Powered by Blockbrain" chip + version stamp */}
      <div className="px-3.5 py-2.5 border-t border-[var(--line)] flex flex-col items-center gap-1">
        <PoweredByBlockbrain compact />
        {version && (
          <Tip label="EDDY build version">
            <span className="text-[length:var(--text-2xs)] text-[var(--ink-3)] tabular-nums">
              {version}
            </span>
          </Tip>
        )}
      </div>
    </aside>
  );
}

/** Icon-only rail — no drag affordances; every visible destination renders
 *  flat, in the same resolved order the expanded list uses (FRONT-2381).
 *  Tooltips are the shared `Tip` component, never the native `title=`
 *  attribute (CLAUDE.md's tooltip rule) — each row also carries its own
 *  `aria-label` for the accessible name, since an icon alone has none. */
function CollapsedSidebar({
  masterStatus,
  onToggleCollapse,
  visibleOrdered,
  theme,
  toggleTheme,
  activeRuns,
  onOnboard,
}: {
  masterStatus: 'alive' | 'off' | 'degraded';
  onToggleCollapse: () => void;
  visibleOrdered: SidebarDestination[];
  theme: string;
  toggleTheme: () => void;
  activeRuns: number | null;
  onOnboard: () => void;
}) {
  return (
    <aside className="w-14 min-w-14 h-full flex flex-col overflow-hidden rounded-[var(--frame-radius)] border border-[var(--line)] bg-[var(--bg-1)] shadow-[var(--shadow-sm)]">
      {/*
        The rail's own identity row (`design_handoff_sidebar_header` §5): too
        narrow for the lockup, so the bars-only mark stands in, with health
        tucked on its corner — the same two facts the expanded header shows,
        in the space available. Matched to the expanded header's 60px so the
        rule under it stays on the same line when the sidebar is toggled.
      */}
      <div className="min-h-[60px] flex items-center justify-center border-b border-[var(--line)]">
        <span className="relative inline-flex shrink-0">
          <EddyMark size={18} />
          <span className="absolute -right-[6px] -bottom-[4px]">
            <HealthDot status={masterStatus} size="sm" />
          </span>
        </span>
      </div>

      <div className="px-2 pt-2">
        <Button
          onClick={onToggleCollapse}
          variant="ghost"
          size="icon"
          className="text-[var(--ink-3)] hover:text-foreground h-9 w-full"
          tooltip="Expand sidebar"
          tooltipSide="right"
          aria-label="Expand sidebar"
        >
          <SidebarSimpleIcon size={16} />
        </Button>
        <Button
          onClick={openCommandPalette}
          variant="ghost"
          size="icon"
          className="text-[var(--ink-3)] hover:text-foreground h-9 w-full mt-1"
          tooltip="Search & commands (⌘K)"
          tooltipSide="right"
          aria-label="Search and commands"
        >
          <MagnifyingGlassIcon size={16} />
        </Button>
      </div>

      {/* FRONT-2381: one flat icon list — no Frequent/More divider. */}
      <div className="flex-1 overflow-y-auto px-1.5 py-2 space-y-0.5">
        {visibleOrdered.map((dest) => (
          <CollapsedRow key={dest.id} dest={dest} />
        ))}
      </div>

      {/* Onboarding, Active runs, theme toggle (FRONT-2381: Active runs moved
       *  here from the scroll region, between the onboarding sparkle button
       *  and the theme toggle, same as the expanded rail's bottom bar). */}
      <div className="border-t border-[var(--line)] flex flex-col items-center gap-1 px-1.5 py-2">
        <Button
          onClick={onOnboard}
          tooltip="Re-run the onboarding wizard"
          tooltipSide="right"
          aria-label="Re-run onboarding"
          variant="ghost"
          size="icon"
          className="text-[var(--ink-2)] hover:text-foreground h-9 w-9"
        >
          <SparkleIcon size={16} />
        </Button>
        {/* Icon-only parity with the pre-FRONT-285 rail; there's no room for
         *  a label + badge at this width, so the count rides the tooltip AND
         *  the `aria-label` (never a native `title`, per the repo's tooltip
         *  rule) — `role="img"` replaces this element's accessible name with
         *  its `aria-label` wholesale, so a bare "Active runs" would read the
         *  count out to nobody. */}
        <Tip label={`Active runs: ${activeRuns ?? '—'}`} side="right">
          <div
            data-testid="sidebar-active-runs"
            role="img"
            aria-label={`Active runs: ${activeRuns ?? '—'}`}
            className="flex items-center justify-center h-9 w-9 text-[var(--ink-3)]"
          >
            <GaugeIcon size={14} weight="regular" />
          </div>
        </Tip>
        <Button
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          tooltip={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          tooltipSide="right"
          variant="ghost"
          size="icon"
          className="text-[var(--ink-2)] hover:text-foreground h-9 w-9"
        >
          {theme === 'dark' ? <SunIcon size={16} /> : <MoonIcon size={16} />}
        </Button>
      </div>
    </aside>
  );
}

export function CollapsedRow({ dest }: { dest: SidebarDestination }) {
  const Icon = dest.icon;
  // Not nested inside another `asChild` trigger, so `Tip` composes onto the
  // `NavLink` directly (CLAUDE.md's tooltip rule: never the native `title=`).
  // `aria-label` carries the accessible name independent of hover/tooltip
  // state — an icon-only link otherwise has none.
  // className MUST be a plain string: `Tip`'s Radix Slot merges className as
  // strings and would stringify a NavLink `({ isActive }) =>` function into the
  // class attribute (FRONT-2782). Active look keys off `aria-current="page"`.
  // Every rail control's label opens to the RIGHT: a label above an icon in a
  // stack of icons covers the one above it (FRONT-2782 — Inbox's hid Search).
  return (
    <Tip label={dest.label} side="right">
      <NavLink
        to={dest.path}
        end={dest.end}
        aria-label={dest.label}
        onClick={(e) => onDestinationClick(e, dest)}
        className={cn(
          'flex items-center justify-center rounded-lg px-1.5 py-2 transition-colors',
          'text-[var(--ink-2)] hover:text-foreground hover:bg-[var(--bg-3)]',
          'aria-[current=page]:bg-primary/10 aria-[current=page]:text-primary',
          'aria-[current=page]:hover:bg-primary/10 aria-[current=page]:hover:text-primary',
        )}
      >
        <Icon size={16} weight="regular" className="shrink-0" />
      </NavLink>
    </Tip>
  );
}
